import BaseComponent from '@/components/base';
import { FontStyle } from '@/components/style';
import Style from '@/components/customization-details/customization-selector/style';

/**
 * CustomizationMultiSelector component: allows users to select multiple customization options.
 *
 * Attributes:
 * - 'selection' (string): A JSON string representing an array of selected items.
 *    Each item should be an object with the following format:
 *   {
 *     "dataKey": "string", // The key of the selected option.
 *     "name": "string"     // The name of the selected option.
 *   }
 * - 'limit' (number): The maximum number of selections allowed.
 */
export default class CustomizationMultiSelector extends BaseComponent {
  static get observedAttributes() {
    return ['selection'];
  }

  afterMount() {
    this.shadowRoot
      .querySelectorAll('.customization-option')
      .forEach((option) => {
        const limit = this.getAttribute('limit');
        option.addEventListener('click', (event) => {
          const element = event.target;
          if (
            element.classList.contains('selected') &&
            this.selection.size > 1
          ) {
            this.removeSelection(element.dataset.key);
          } else if (this.selection.size < limit) {
            this.addNewSelection(
              element.dataset.key,
              element.textContent.trim()
            );
          } else if (this.selection.size == limit) {
            this.replaceSelection(
              element.dataset.key,
              element.textContent.trim()
            );
          }
          if (this.handleOptionClick) {
            this.handleOptionClick(element.dataset.key);
          }
        });
      });
  }

  /**
   * Retrieves the selection attribute, parses it as JSON, and converts it into a Map.
   *
   * @returns {Map<string, string>} A map where the keys are data keys and the values are names.
   */
  get selection() {
    const selectedArray = JSON.parse(this.getAttribute('selection'));
    const selectedMap = new Map(
      selectedArray.map((item) => [item.dataKey, item.name])
    );
    return selectedMap;
  }

  /**
   * Retrieves the names of the current selections.
   *
   * @returns {string} A comma-separated string of selection names.
   */
  get selectionNames() {
    return [...this.selection.values()].join(', ');
  }

  /**
   * Gets the keys of the current selection.
   *
   * @returns {Array} An array containing the keys of the current selection.
   */
  get selectionKeys() {
    return [...this.selection.keys()];
  }

  /**
   * Adds a new selection to the current selection list and updates the 'selection' attribute to trigger a re-render
   *
   * @param {string} dataKey - The key associated with the new selection.
   * @param {string} name - The name associated with the new selection.
   * @returns {void}
   */
  addNewSelection(dataKey, name) {
    const selected = [...this.selection]?.map(([key, value]) => {
      return {
        dataKey: key,
        name: value
      };
    });
    selected.push({
      dataKey,
      name
    });
    this.setAttribute('selection', JSON.stringify(selected));
  }

  /**
   * Removes an item from the selection based on the provided data key.
   *
   * @param {string} dataKey - The key of the item to be removed from the selection.
   * @returns {void}
   */
  removeSelection(dataKey) {
    const selected = [...this.selection]
      ?.map(([key, value]) => {
        return {
          dataKey: key,
          name: value
        };
      })
      .filter((item) => item.dataKey !== dataKey);
    this.setAttribute('selection', JSON.stringify(selected));
  }

  /**
   * Replaces the current selection with a new selection.
   *
   * This method removes the first key from the current selection (if it exists)
   * and then adds a new selection with the provided data key and name.
   *
   * @param {string} dataKey - The key associated with the new selection.
   * @param {string} name - The name associated with the new selection.
   * @returns {void}
   */
  replaceSelection(dataKey, name) {
    const firstKey = this.selection.keys().next().value;
    if (firstKey !== undefined) {
      this.removeSelection(firstKey);
    }
    this.addNewSelection(dataKey, name);
  }

  render() {
    return `
      ${FontStyle}
      ${Style}
      <section>
        <p>New Selection: ${this.selectionNames}</p>
        <ul class="customization-option-wrapper">
          ${this.data?.options?.value
            ?.map((option) => {
              return `
              <li data-key="${option.dataKey}" class="customization-option ${
                this.selection.has(option.dataKey) ? 'selected' : ''
              }">
                ${option.name}
              </li>`;
            })
            .join('')}
          </ul>
      </section>
    `;
  }
}
